import type React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Box, Grid2, type AlertColor } from '@mui/material';
import CustomCard from '@qler-view/shared_lib/src/shared/lib/ui-components/Card/CustomCard';
import CustomCardContent from '@qler-view/shared_lib/src/shared/lib/ui-components/Card/CustomCardContent';
import CustomTypography from '@qler-view/shared_lib/src/shared/lib/ui-components/Typography/CustomTypography';
import CustomDivider from '@qler-view/shared_lib/src/shared/lib/ui-components/Divider/CustomDivider';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConsultMoreDetails } from '../../API/hooks/consultMoreDetailsHook';
import CustomTabs from '@qler-view/shared_lib/src/shared/lib/ui-components/Tabs/CustomTabs';
import CustomTabPanel from '@qler-view/shared_lib/src/shared/lib/ui-components/Tabs/CustomTabPanel';
import PastSession from './PastSession';
import { CustomIconButton } from '@qler-view/shared_lib/src';
import ChatIcon from '@assets/images/chat.svg';
import { CallStatusEnum } from '../components/ConsultStatusChip';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import BackIcon from '@assets/images/BackIcon.svg';
import ActivateIcon from '@assets/images/ActivateIcon.svg';
import {
  CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
  CONSULT_WAITING_SCREEN_ROUTE_PATH,
  FACILITY_CONSULT_CREATE_DASHBOARD_ROUTE_PATH,
} from '../../routers/RoutPath';
import ActivateConsultConfirmationPopup from '../dashboard/ActivateConsultConfirmationPopup';
import { useActivateConsult } from '../../API/hooks/caseManagerHook';
import { getUserFromStorage } from '../components/storageUtility';
import CustomSnackbar from '@qler-view/shared_lib/src/shared/lib/ui-components/Snackbar/CustomSnackbar';
import ChatQLER from '@qler-view/shared_lib/src/shared/Chat/ChatQLER';
import {
  useGetAcsChatToken,
  useGetConsultChatThread,
} from '../../API/hooks/acsChatHook';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src/shared/constants/portalType';
import CustomBadge from '@qler-view/shared_lib/src/shared/lib/ui-components/Badge/CustomBadge.js';
import CustomLink from '@qler-view/shared_lib/src/shared/lib/ui-components/Link/CustomLink';
import {
  formatFieldValue,
  isValidUrl,
} from '@qler-view/shared_lib/src/reusableFunctions/CommonFunctions';
import CancelConsultModel, {
  type CancelReasonFacilitySubmitParams,
  type CancelReasonFacilityParams,
} from '@qler-view/shared_lib/src/shared/statusrequests/CancelConsultModel';
import {
  useCancelConsultMetadata,
  useSaveCancelConsult,
} from '../../shared/statusRequest/StatusRequestSetup';
import TimeTicker from '@qler-view/shared_lib/src/shared/reusableComponents/TimeTicker';
import TimeDifference from '@qler-view/shared_lib/src/shared/reusableComponents/TimeDifference';
import AscensionLogo from '@assets/images/ascension_logo.svg';
import { useResumeSelectedConsults } from '../../API/hooks/resumeConsultHook';

export const getButtonName = (sessionStatus: string | null): string => {
  if (!sessionStatus) return '';

  if (sessionStatus.toLowerCase() === CallStatusEnum.OnHold) {
    return 'Activate';
  }

  return 'Cancel';
};

const renderTimer = (
  status: string | undefined,
  createdOn: string | null,
  waitEndTime: string
) => {
  const normalizedStatus = status?.toLowerCase();
  const isActiveStatus =
    normalizedStatus === CallStatusEnum.New ||
    normalizedStatus === CallStatusEnum.InProgress ||
    normalizedStatus === CallStatusEnum.OnHold;

  return (
    <Box className="flex clock-text pb-4">
      {isActiveStatus ? (
        <TimeTicker createdOn={createdOn} />
      ) : (
        <TimeDifference
          createdOn={createdOn ?? ''}
          waitEndTime={waitEndTime ?? ''}
        />
      )}
    </Box>
  );
};

const shouldShowActivateButton = (
  status: string | undefined,
  requestedByOthers: boolean | undefined
) => {
  return !requestedByOthers && status?.toLowerCase() === CallStatusEnum.OnHold;
};

const shouldShowCancelButton = (
  status: string | undefined,
  requestedByOthers: boolean | undefined
) => {
  const normalizedStatus = status?.toLowerCase();
  return (
    !requestedByOthers &&
    normalizedStatus !== CallStatusEnum.Completed &&
    normalizedStatus !== CallStatusEnum.CallCompleted
  );
};

const shouldShowJoinButton = (
  status: string | undefined,
  requestedByOthers: boolean | undefined,
  isCustomUrl: boolean | undefined
) => {
  const normalizedStatus = status?.toLowerCase();
  return (
    !requestedByOthers &&
    (normalizedStatus === CallStatusEnum.New ||
      normalizedStatus === CallStatusEnum.InProgress ||
      normalizedStatus === CallStatusEnum.CallStarted) &&
    !isCustomUrl
  );
};

const shouldShowEditButton = (
  status: string | undefined,
  requestedByOthers: boolean | undefined
) => {
  const normalizedStatus = status?.toLowerCase();
  return (
    !requestedByOthers &&
    (normalizedStatus === CallStatusEnum.New ||
      normalizedStatus === CallStatusEnum.OnHold)
  );
};

const ConsultMoreDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUserFromStorage();
  const consultId = location.state?.consultId ?? 0;
  const {
    consultMoreDetails,
    isLoading,
    refetch: refetchConsultMoreDetails,
  } = useConsultMoreDetails(Number(consultId), user?.userId ?? 0);

  const [activeTab, setActiveTab] = useState<number | string>(
    location.state?.cameFromBack ? 1 : 0
  );
  const [openCancelRequest, setOpenCancelRequest] = useState(false);
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState(false);
  const MessageRef = useRef('');
  const [messageType, setMessageType] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

  const { CancelConsultMetadata: ConsultCancelMetaData } =
    useCancelConsultMetadata(PORTAL_TYPES.FACILITY);

  const { acsChatToken } = useGetAcsChatToken(
    user?.userId ?? 0,
    PORTAL_TYPES.FACILITY
  );
  const { consultChatThread } = useGetConsultChatThread(
    Number(consultId) || 0,
    user?.userId ?? 0,
    PORTAL_TYPES.FACILITY,
    isChatOpen
  );

  const { mutate: cancelConsult } = useSaveCancelConsult();

  const handleResumeSuccess = () => {
    navigate(
      `${CONSULT_WAITING_SCREEN_ROUTE_PATH}?consultId=${consultMoreDetails?.id}`,
      { replace: true }
    );
  };

  const { mutate: resumeConsult } =
    useResumeSelectedConsults(handleResumeSuccess);

  const tabItems = [
    { label: 'Facility Fields', value: 0 },
    {
      label: `Past Consults (${consultMoreDetails?.pastSessionCount})`,
      value: 1,
    },
  ];

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number | string
  ) => {
    setActiveTab(newValue);
  };

  const handelConsultEdit = () => {
    return navigate(
      `${FACILITY_CONSULT_CREATE_DASHBOARD_ROUTE_PATH}/${consultId}`
    );
  };

  const onActivateSuccess = () => {
    refetchConsultMoreDetails();
    setOpenConfirmationPopup(false);
    setMessageType('success');
    MessageRef.current =
      'Consult Request has been successfully moved to the New status.';
    setOpenToast(true);
  };

  const { mutate: activate } = useActivateConsult(
    user?.userId ?? 0,
    onActivateSuccess
  );

  useEffect(() => {
    if (!consultId) {
      navigate(CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH, {
        replace: true,
      });
    }
  }, [consultId, navigate]);

  useEffect(() => {
    const consultStatus = consultMoreDetails?.sessionStatus;
    if (
      consultStatus?.toLowerCase() === CallStatusEnum.Abandoned ||
      consultMoreDetails?.isFacilityChanged
    ) {
      navigate(CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH, {
        replace: true,
      });
    }
  }, [consultMoreDetails]);

  useEffect(() => {
    if (location.state?.chatOpen) {
      setIsChatOpen(true);
    }
  }, [location.state?.chatOpen]);

  const renderFieldValue = (fieldLabel: string, fieldValue: string) => {
    if (isValidUrl(fieldValue)) {
      const href =
        fieldValue?.startsWith('http://') || fieldValue?.startsWith('https://')
          ? fieldValue
          : `https://${fieldValue}`;
      return (
        <CustomLink
          id={`lbl-${fieldLabel}-value`}
          variant="body3"
          className="content-align"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          color="primary"
        >
          {fieldValue}
        </CustomLink>
      );
    }
    return (
      <CustomTypography
        id={`lbl-${fieldLabel}-value`}
        variant="body3"
        className="content-align"
      >
        {formatFieldValue(fieldValue)}
      </CustomTypography>
    );
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  const status = consultMoreDetails?.sessionStatus?.toLowerCase();

  const handleSubmitCancelReason = (data: CancelReasonFacilityParams) => {
    const mappedTechnicalDifficultiesId =
      [
        data.patientAdmittedId,
        data.noLongerRequiredId,
        data.patientDischargedId,
        data.technicalDifficultiesId,
      ].find((id) => id !== 0) ?? 0;

    const payload: CancelReasonFacilitySubmitParams = {
      cancelReasonId: data.cancelReasonId,
      technicalDifficultiesId: mappedTechnicalDifficultiesId,
      otherReason: data.otherReason,
    };
    cancelConsult(
      {
        userId: user?.userId.toString() ?? '',
        portalType: PORTAL_TYPES.FACILITY,
        consultId,
        ...payload,
      },
      {
        onSuccess: () => {
          console.log('on success data', data);

          setOpenToast(true);
          setTimeout(() => {
            setOpenToast(false);
            navigate(CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH, {
              replace: true,
              state: { message: 'The consult request has been canceled.' },
            });
          }, 3000);
          setOpenCancelRequest(false);
        },
      }
    );
  };

  const setViewOnly = (status?: string) => {
    const normalizedStatus = status?.toLowerCase();

    const isClosed =
      normalizedStatus === CallStatusEnum.Completed.toLowerCase() ||
      normalizedStatus === CallStatusEnum.Cancelled.toLowerCase() ||
      location.state?.requestedByOthers === true;

    return isClosed;
  };

  return (
    <Box>
      {openCancelRequest && (
        <CancelConsultModel
          openCancelConsultPopup={openCancelRequest}
          onCloseCancelConsultPopup={() => setOpenCancelRequest(false)}
          ConsultCancelMetaData={ConsultCancelMetaData}
          onSubmitCancelConsult={(values) => {
            handleSubmitCancelReason(values);
          }}
          portal={PORTAL_TYPES.FACILITY}
        />
      )}

      {openToast && (
        <CustomSnackbar
          id="activate-toast"
          successOrError={messageType as AlertColor}
          open={openToast}
          handleClose={() => setOpenToast(false)}
          toastrMessage={MessageRef.current}
        />
      )}

      <Box>
        <ActivateConsultConfirmationPopup
          openPopup={openConfirmationPopup}
          onClosePopup={() => {
            setOpenConfirmationPopup(false);
          }}
          onConfirm={() => {
            activate({ consultId: consultId });
          }}
        />
      </Box>
      <Box className="flex">
        <Grid2 container spacing={3} className="width-100">
          <Grid2
            size={{
              xs: 12,
              sm: 12,
              md: isChatOpen ? 8.5 : 12,
              lg: isChatOpen ? 8.5 : 12,
              xl: isChatOpen ? 8.5 : 12,
            }}
          >
            <Box>
              <Grid2 container>
                <Grid2 size={{ xs: 12 }} className="pb-24">
                  <CustomCard
                    id="facility-patient-details"
                    className="width-100"
                  >
                    <CustomCardContent
                      id="facility-patient-details-content"
                      className="card-content"
                    >
                      <Box className="flex  align-items-center pb-24">
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 5.9, lg: 5.9, xl: 5.9 }}
                        >
                          <Box className="flex align-items-center">
                            <CustomIconButton
                              id="back-button"
                              icon={<BackIcon />}
                              onClick={() => {
                                navigate(
                                  CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH
                                );
                              }}
                            ></CustomIconButton>
                            <CustomTypography
                              id="patient-name"
                              variant="h4"
                              className="text-align-justify pl-8"
                            >
                              {consultMoreDetails?.patientName}
                            </CustomTypography>
                          </Box>
                        </Grid2>
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 6.1, lg: 6.1, xl: 6.1 }}
                        >
                          <Box className="float-right flex align-items-center">
                            {consultMoreDetails?.isExternalConsult !== null &&
                              consultMoreDetails?.isExternalConsult && (
                                <Box className="pr-8">
                                  <AscensionLogo />
                                </Box>
                              )}
                            <Box>
                              {renderTimer(
                                consultMoreDetails?.sessionStatus,
                                consultMoreDetails?.createdOn || null,
                                consultMoreDetails?.waitEndTime ?? ''
                              )}
                            </Box>
                            <Box className="icon-background-grey ml-12">
                              <CustomBadge
                                id={`${consultMoreDetails?.id}-chat-notification`}
                                badgeContent={
                                  consultMoreDetails?.unreadMessageCount
                                }
                                color="error"
                                max={99}
                              >
                                <CustomIconButton
                                  id="icon-chat"
                                  icon={<ChatIcon />}
                                  onClick={() => {
                                    setIsChatOpen(!isChatOpen);
                                  }}
                                ></CustomIconButton>
                              </CustomBadge>
                            </Box>
                            {shouldShowActivateButton(
                              status,
                              location.state?.requestedByOthers
                            ) && (
                              <Box className="ml-12">
                                <CustomButton
                                  id="btnAction"
                                  startIcon={<ActivateIcon />}
                                  variant={'contained'}
                                  className="button-style"
                                  color={'success'}
                                  size="large"
                                  onClick={() => {
                                    setOpenConfirmationPopup(true);
                                  }}
                                >
                                  Activate
                                </CustomButton>
                              </Box>
                            )}

                            {shouldShowJoinButton(
                              status,
                              location.state?.requestedByOthers,
                              consultMoreDetails?.isCustomUrl
                            ) && (
                              <Box className="ml-12">
                                <CustomButton
                                  id="btnJoin"
                                  variant="contained"
                                  color="primary"
                                  size="large"
                                  onClick={() => {
                                    resumeConsult({
                                      userId: user?.userId ?? 0,
                                      consultId: Number(consultMoreDetails?.id),
                                    });
                                  }}
                                >
                                  Join
                                </CustomButton>
                              </Box>
                            )}

                            {shouldShowEditButton(
                              status,
                              location.state?.requestedByOthers
                            ) && (
                              <Box className="ml-12">
                                <CustomButton
                                  id="btnAction"
                                  variant="contained"
                                  className="button-style"
                                  color={'primary'}
                                  size="small"
                                  onClick={handelConsultEdit}
                                >
                                  Edit
                                </CustomButton>
                              </Box>
                            )}
                            {shouldShowCancelButton(
                              status,
                              location.state?.requestedByOthers
                            ) && (
                              <Box className="ml-12">
                                <CustomButton
                                  id="btnAction"
                                  variant={'outlined'}
                                  className="button-style"
                                  color={'error'}
                                  size="large"
                                  onClick={() => {
                                    setOpenCancelRequest(true);
                                  }}
                                >
                                  Cancel
                                </CustomButton>
                              </Box>
                            )}
                          </Box>
                        </Grid2>
                      </Box>

                      <CustomDivider />
                      <Box className="pt-24">
                        <Box>
                          <Grid2
                            container
                            spacing={0.5}
                            className="text-align-left"
                          >
                            {consultMoreDetails?.sessionFields.map(
                              (field, index) => (
                                <Grid2
                                  key={`${field.fieldLabel}-${index}`}
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                  className="text-left pb-24"
                                >
                                  <Box>
                                    <CustomTypography
                                      id={`lbl-${field.fieldLabel}-${index}-label`}
                                      variant="body2"
                                      color="textSecondary"
                                      className="pb-4"
                                    >
                                      {field.fieldLabel}
                                    </CustomTypography>
                                    <CustomTypography
                                      id={`lbl-${field.fieldLabel}-${index}-value`}
                                      variant="body2"
                                    >
                                      {formatFieldValue(field.fieldValue, {
                                        isDateOfBirth:
                                          field.fieldLabel === 'Date Of Birth',
                                      })}
                                    </CustomTypography>
                                  </Box>
                                </Grid2>
                              )
                            )}
                          </Grid2>
                        </Box>
                      </Box>
                    </CustomCardContent>
                  </CustomCard>
                </Grid2>
              </Grid2>
              <Grid2 container>
                <Grid2 size={{ xs: 12 }} className="pb-24">
                  <CustomCard
                    id="facility-additional-consult-fields"
                    className="width-100"
                  >
                    <CustomCardContent
                      id="facility-additional-consult-fields-content"
                      className="card-content"
                    >
                      <Box className="flex align-items-center pb-24">
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 5.9, lg: 5.9, xl: 5.9 }}
                        >
                          <CustomTypography
                            id="additional-consult-fields-title"
                            variant="h4"
                            className="text-align-justify"
                          >
                            Additional Information
                          </CustomTypography>
                        </Grid2>
                      </Box>
                      <CustomDivider />
                      <Box className="pt-24">
                        <Box>
                          <Grid2
                            container
                            spacing={0.5}
                            className="text-align-left"
                          >
                            {consultMoreDetails &&
                            consultMoreDetails.additionalSessionFields.length >
                              0 ? (
                              consultMoreDetails.additionalSessionFields.map(
                                (field, index) => (
                                  <Grid2
                                    key={`${field.fieldLabel}-${index}`}
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 4,
                                      lg: 2.4,
                                      xl: 2.4,
                                    }}
                                    className="text-left pb-24"
                                  >
                                    <Box>
                                      <CustomTypography
                                        id={`lbl-${field.fieldLabel}-label`}
                                        variant="body2"
                                        color="textSecondary"
                                        className="pb-4"
                                      >
                                        {field.fieldLabel}
                                      </CustomTypography>
                                      {isValidUrl(field.fieldValue) ? (
                                        <CustomLink
                                          id={`lbl-${field.fieldLabel}-value`}
                                          variant="body2"
                                          className="content-align"
                                          href={
                                            field.fieldValue?.startsWith(
                                              'http://'
                                            ) ||
                                            field.fieldValue?.startsWith(
                                              'https://'
                                            )
                                              ? field.fieldValue
                                              : `https://${field.fieldValue}`
                                          }
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          underline="hover"
                                          color="primary"
                                        >
                                          {field.fieldValue}
                                        </CustomLink>
                                      ) : (
                                        <CustomTypography
                                          id={`lbl-${field.fieldLabel}-value`}
                                          variant="body2"
                                          className="content-align"
                                        >
                                          {formatFieldValue(field.fieldValue)}
                                        </CustomTypography>
                                      )}
                                    </Box>
                                  </Grid2>
                                )
                              )
                            ) : (
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                                className="button-width"
                              >
                                <Box className="text-align-center no-consults-data">
                                  <CustomTypography
                                    variant="body2"
                                    color="textDisabled"
                                    id="noadditionalFieldsAvailable"
                                  >
                                    No Additional Fields Available
                                  </CustomTypography>
                                </Box>
                              </Grid2>
                            )}
                          </Grid2>
                        </Box>
                      </Box>
                    </CustomCardContent>
                  </CustomCard>
                </Grid2>
              </Grid2>
              <Grid2 container>
                <Grid2 size={{ xs: 12 }} className="pb-24">
                  <CustomCard id="facility-details-tab" className="width-100">
                    <CustomCardContent
                      id="facility-details-tab-content"
                      className="card-content"
                    >
                      <Box>
                        <CustomTabs
                          id="facility-more-details-tab"
                          tabs={tabItems}
                          value={activeTab}
                          onChange={handleTabChange}
                          indicatorColor="primary"
                          textColor="primary"
                          variant="scrollable"
                          allowScrollButtonsMobile={true}
                          orientation="horizontal"
                        />
                        <CustomDivider />
                      </Box>
                      <CustomDivider />
                      <Box>
                        <CustomTabPanel
                          value={activeTab}
                          index={0}
                          className="pb-none"
                        >
                          <Box>
                            <Grid2
                              container
                              spacing={0.5}
                              className="text-align-left"
                            >
                              {consultMoreDetails &&
                              consultMoreDetails.facilityDetails.length > 0 ? (
                                consultMoreDetails.facilityDetails.map(
                                  (field, index) => {
                                    return (
                                      <Grid2
                                        key={`${field.fieldLabel}-${index}`}
                                        size={{
                                          xs: 12,
                                          sm: 12,
                                          md: 4,
                                          lg: 2.4,
                                          xl: 2.4,
                                        }}
                                        className="text-left pb-24"
                                      >
                                        <Box>
                                          <CustomTypography
                                            id={`lbl-${field.fieldLabel}-label`}
                                            variant="body2"
                                            color="textSecondary"
                                            className="pb-4"
                                          >
                                            {field.fieldLabel}
                                          </CustomTypography>
                                          {renderFieldValue(
                                            field.fieldLabel,
                                            field.fieldValue
                                          )}
                                        </Box>
                                      </Grid2>
                                    );
                                  }
                                )
                              ) : (
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 4,
                                    lg: 2.4,
                                    xl: 2.4,
                                  }}
                                  className="button-width"
                                >
                                  <Box className="text-align-center no-consults-data">
                                    <CustomTypography
                                      variant="body2"
                                      color="textDisabled"
                                      id="no-details-available-text"
                                    >
                                      No Details Available
                                    </CustomTypography>
                                  </Box>
                                </Grid2>
                              )}
                            </Grid2>
                          </Box>
                        </CustomTabPanel>
                        <CustomTabPanel value={activeTab} index={1}>
                          <CustomTypography
                            id="past-sessions-tab-content"
                            variant="body2"
                          >
                            <Box>
                              <Grid2 container>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12,
                                  }}
                                  className="text-left"
                                >
                                  <PastSession />
                                </Grid2>
                              </Grid2>
                            </Box>
                          </CustomTypography>
                        </CustomTabPanel>
                      </Box>
                    </CustomCardContent>
                  </CustomCard>
                </Grid2>
              </Grid2>
            </Box>
          </Grid2>
          <Grid2
            size={{
              xs: 12,
              sm: 12,
              md: isChatOpen ? 3.5 : 0,
              lg: isChatOpen ? 3.5 : 0,
              xl: isChatOpen ? 3.5 : 0,
            }}
          >
            <Box className="pb-24">
              <ChatQLER
                onClose={() => setIsChatOpen(false)}
                acsConfig={
                  acsChatToken
                    ? {
                        endpoint: import.meta.env.VITE_ACS_ENDPOINT,
                        token: acsChatToken.token,
                        userId: acsChatToken.acsUserId,
                        displayName:
                          fullName === ''
                            ? (user?.username ?? 'User')
                            : fullName,
                        portalType: PORTAL_TYPES.FACILITY,
                      }
                    : undefined
                }
                isViewOnly={setViewOnly(status)}
                threadId={consultChatThread?.threadId}
                subtitle={consultChatThread?.participants || ''}
                hidden={!isChatOpen}
              />
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default ConsultMoreDetails;
